/* eslint-disable no-console */
import CustomModalWrapper from 'components/CustomModalWrapper/CustomModalWrapper';
import IconButton from 'components/IconButton';
import Markdown from 'components/Markdown';
import ModalDialog from 'components/ModalDialog';
import Skeleton from 'components/Skeleton';
import Tooltip from 'components/Tooltip';
import { EditorState, convertToRaw, convertFromRaw } from 'draft-js';
import { useAtomValue, useSetAtom } from 'jotai';
import callApi from 'lib/callApi';
import {
  showFailureToast,
  showSuccessToast,
  showWarningToast,
} from 'lib/ToastHelper';
import { draftToMarkdown, markdownToDraft } from 'markdown-draft-js';
import moment from 'moment';
import React, { useState, useEffect } from 'react';
import './PCNotesModal.css';
import { CircleSpinner } from 'react-spinners-kit';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
} from 'reactstrap';
import { companyIdAtom } from 'views/state';

import GenericRichEditor from '../../../components/GenericRichEditor/GenericRichEditor';
import { findingGridDataRefreshAtom } from '../state';

interface NotesModalProps {
  toggle: () => void;
  postureId: number;
  haveUnreadNotes: boolean;
  title: string;
}

const PCNotesModal: React.FC<NotesModalProps> = ({
  toggle,
  postureId,
  haveUnreadNotes,
  title,
}) => {
  // States for notes management
  const [notes, setNotes] = useState<any[]>([]);
  const [highlightedNoteIds, setHighlightedNoteIds] = useState<number[]>([]);
  const [shouldHighlight, setShouldHighlight] = useState(true);
  const [loading, setLoading] = useState(false);
  const [addingNote, setAddingNote] = useState(false);
  const [editingNote, setEditingNote] = useState(false);
  const [deletingNote, setDeletingNote] = useState(false);

  // States for rich text editor
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [editorEditedState, setEditorEditedState] = useState(
    EditorState.createEmpty(),
  );
  const [editedNoteId, setEditedNoteId] = useState<number | null>(null);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<number | null>(null);

  // Atoms for state management
  const companyId = useAtomValue(companyIdAtom);
  const findingGridDataRefresh = useSetAtom(findingGridDataRefreshAtom);

  // Fetch notes when modal opens
  useEffect(() => {
    if (postureId) {
      fetchNotes();
      // Reset editor state when modal opens
      setEditorState(EditorState.createEmpty());
    }
  }, [postureId]);

  // Highlight unread notes for 10 seconds when notes are loaded
  useEffect(() => {
    if (notes && notes.length > 0 && shouldHighlight) {
      const unreadIds = notes
        .filter((n) => n.is_read === false)
        .map((n) => n.id);
      if (unreadIds.length > 0) {
        setHighlightedNoteIds(unreadIds);
        const timeout = setTimeout(() => {
          setHighlightedNoteIds([]);
          setShouldHighlight(false);
        }, 6000); // 6 seconds
        return () => clearTimeout(timeout);
      }
    }
  }, [notes, shouldHighlight]);

  // Function to fetch notes for a posture
  const fetchNotes = () => {
    setLoading(true);

    // Get notes for the posture
    callApi(`/pcm/posture-notes/`, 'GET', {
      posture: postureId,
      company_id: companyId,
    })
      .then((res) => {
        if (res.status === 200) {
          setNotes(res.data.results || []);

          // If there are unread notes, mark them as read
          if (haveUnreadNotes) {
            markAllAsRead();
          }
        } else {
          showFailureToast('Error fetching notes');
        }
      })
      .catch((error) => {
        console.error('Error fetching notes:', error);
        showFailureToast('Failed to load notes');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Helper function to mark all notes as read
  const markAllAsRead = () => {
    callApi(
      `/pcm/posture-notes/mark-all-as-read/`,
      'POST',
      {},
      {
        posture_ids: [postureId],
        company_id: companyId,
      },
    )
      .then(() => {
        // Refresh the grid data to update unread counts
        findingGridDataRefresh(Math.random().toString());
      })
      .catch((error) => {
        console.error('Error marking notes as read:', error);
      });
  };

  // Function to add a new note
  const addNote = () => {
    const contentState = editorState.getCurrentContent();
    const rawContent = convertToRaw(contentState);
    const markdownContent = draftToMarkdown(rawContent);

    if (!markdownContent.trim()) {
      showWarningToast('Note content cannot be empty');
      return;
    }

    setAddingNote(true);

    callApi(
      '/pcm/posture-notes/',
      'POST',
      {},
      {
        posture: postureId,
        content: markdownContent,
        company_id: companyId,
      },
    )
      .then((res) => {
        if (res.status === 201 || res.status === 200) {
          // Add the new note to the state directly instead of refetching all notes
          const newNote = res.data;
          setNotes([newNote, ...notes]);
          setEditorState(EditorState.createEmpty());
          showSuccessToast('Note added successfully');
          // Refresh the grid data to update unread counts
          findingGridDataRefresh(Math.random().toString());
        } else {
          showFailureToast('Failed to add note');
        }
      })
      .catch((error) => {
        console.error('Error adding note:', error);
        showFailureToast('Failed to add note');
      })
      .finally(() => {
        setAddingNote(false);
      });
  };

  // Function to start editing a note
  const startEditNote = (note: any) => {
    setEditedNoteId(note.id);
    const contentState = markdownToDraft(note.content);
    setEditorEditedState(
      EditorState.createWithContent(convertFromRaw(contentState)),
    );
    setEditingNote(true);
  };

  // Function to cancel editing
  const cancelEdit = () => {
    setEditedNoteId(null);
    setEditingNote(false);
  };

  // Function to save edited note
  const saveEditedNote = () => {
    if (!editedNoteId) return;

    const contentState = editorEditedState.getCurrentContent();
    const rawContent = convertToRaw(contentState);
    const markdownContent = draftToMarkdown(rawContent);

    if (!markdownContent.trim()) {
      showWarningToast('Note content cannot be empty');
      return;
    }

    setEditingNote(true);

    callApi(
      `/pcm/posture-notes/${editedNoteId}/`,
      'PATCH',
      {},
      {
        content: markdownContent,
        company_id: companyId,
      },
    )
      .then((res) => {
        if (res.status === 200) {
          const updatedNotes = notes.map((note) =>
            note.id === editedNoteId ? { ...note, ...res.data } : note,
          );
          setNotes(updatedNotes);
          setEditedNoteId(null);
          showSuccessToast('Note updated successfully');
        } else {
          showFailureToast('Failed to update note');
        }
      })
      .catch((error) => {
        console.error('Error updating note:', error);
        showFailureToast('Failed to update note');
      })
      .finally(() => {
        setEditingNote(false);
      });
  };

  // Function to confirm delete note
  const confirmDeleteNote = (noteId: number) => {
    setNoteToDelete(noteId);
    setShowConfirmDeleteModal(true);
  };

  // Function to cancel delete
  const cancelDelete = () => {
    setNoteToDelete(null);
    setShowConfirmDeleteModal(false);
  };

  // Function to delete note
  const deleteNote = () => {
    if (!noteToDelete) return;

    setDeletingNote(true);

    callApi(`/pcm/posture-notes/${noteToDelete}/`, 'DELETE', {
      company_id: companyId,
    })
      .then(() => {
        const updatedNotes = notes.filter((note) => note.id !== noteToDelete);
        setNotes(updatedNotes);
        setShowConfirmDeleteModal(false);
        setNoteToDelete(null);
        showSuccessToast('Note deleted successfully');
        // Refresh the grid data to update unread counts
        findingGridDataRefresh(Math.random().toString());
      })
      .catch((error) => {
        console.error('Error deleting note:', error);
        showFailureToast('Failed to delete note');
      })
      .finally(() => {
        setDeletingNote(false);
      });
  };

  // Render delete confirmation modal
  const renderDeleteConfirmModal = () => {
    return (
      <Modal isOpen={showConfirmDeleteModal} size="sm" zIndex={9999}>
        <ModalHeader>
          <h4>Delete Confirmation</h4>
        </ModalHeader>
        <ModalBody>
          <p>Are you sure you want to delete this Note?</p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={cancelDelete}>
            Cancel
          </Button>
          <Button
            color="danger"
            disabled={deletingNote}
            onClick={deleteNote}
            style={{ minWidth: '80px' }}
          >
            {deletingNote ? (
              <div className="d-flex align-items-center justify-content-center">
                <CircleSpinner size={20} />
                <span className="ml-1">Deleting...</span>
              </div>
            ) : (
              'Delete'
            )}
          </Button>
        </ModalFooter>
      </Modal>
    );
  };

  // Check if the current user is the author of a note
  const isCurrentUserAuthor = (authorEmail: string) => {
    const currentUserEmail = localStorage.getItem('loggedInUser');
    return authorEmail === currentUserEmail;
  };

  // Check if a note has been modified by comparing created_at and updated_at timestamps
  const isNoteModified = (note: any) => {
    // Parse the timestamps and compare them up to the second (ignoring milliseconds)
    const createdDate = moment(note.created_at).format('YYYY-MM-DD HH:mm:ss');
    const updatedDate = moment(note.updated_at).format('YYYY-MM-DD HH:mm:ss');

    // Return true if the timestamps are different (note was modified)
    return createdDate !== updatedDate;
  };

  // Render skeleton placeholders for notes
  const renderSkeletons = () => {
    return Array(2)
      .fill(0)
      .map((_, index) => (
        <Card key={`skeleton-${index}`} className="mb-3 shadow-sm">
          <CardHeader className="d-flex justify-content-between align-items-center bg-light mb-2">
            <div className="d-flex align-items-center">
              <Skeleton width="32px" height="32px" rounded="full" />
              <div className="ml-2">
                <Skeleton width="120px" height="18px" rounded="md" />
              </div>
            </div>
            <div>
              <Skeleton width="60px" height="24px" rounded="md" />
            </div>
          </CardHeader>
          <CardBody>
            <Skeleton width="100%" height="20px" rounded="md" />
            <Skeleton
              width="90%"
              height="20px"
              rounded="md"
              style={{ marginTop: '8px' }}
            />
            <Skeleton
              width="80%"
              height="20px"
              rounded="md"
              style={{ marginTop: '8px' }}
            />
          </CardBody>
        </Card>
      ));
  };

  // Render notes content
  const renderNotesContent = () => {
    if (loading) {
      return renderSkeletons();
    }

    if (notes.length === 0) {
      return (
        <div className="text-center py-5">
          <div className="mb-3">
            <i
              className="tim-icons icon-chat-33 text-muted"
              style={{ fontSize: '3rem' }}
            ></i>
          </div>
          <h5 className="text-muted">No notes yet</h5>
          <p className="text-muted">
            Be the first to add a note to this finding.
          </p>
        </div>
      );
    }

    return notes.map((note) => (
      <Card
        key={note.id}
        className={`mb-3 shadow-sm${
          highlightedNoteIds.includes(note.id) ? ' note-highlight-fade' : ''
        }`}
      >
        <CardHeader className="d-flex justify-content-between align-items-center bg-light">
          <div className="d-flex align-items-center">
            <div
              className="avatar bg-primary text-white rounded-circle d-flex align-items-center justify-content-center mr-2"
              style={{ width: '32px', height: '32px' }}
            >
              {note.author.username.charAt(0).toUpperCase()}
            </div>
            <div className="ml-2">
              <div className="d-flex align-items-center">
                <div className="font-weight-bold mr-2">
                  {note.author.username}
                </div>
              </div>
              <div>
                <small className="text-muted">
                  {moment(note.created_at).format('MMM D, YYYY h:mm A')}
                </small>
                {isNoteModified(note) && (
                  <Tooltip
                    tooltip={
                      <div>
                        <div className="font-weight-bold mb-1">
                          Note was edited
                        </div>
                        <div>
                          Original:{' '}
                          {moment(note.created_at).format('MMM D, YYYY h:mm A')}
                        </div>
                        <div>
                          Modified:{' '}
                          {moment(note.updated_at).format('MMM D, YYYY h:mm A')}
                        </div>
                      </div>
                    }
                    place="bottom"
                  >
                    <span
                      className="badge badge-pill ml-2"
                      style={{ fontSize: '10px', padding: '3px 6px' }}
                    >
                      <i className="fas fa-pen mr-1"></i>
                      edited
                    </span>
                  </Tooltip>
                )}
              </div>
            </div>
          </div>
          {editedNoteId === note.id ? (
            <div>
              <IconButton
                iconClass="fas fa-check"
                tooltip="Update Note"
                onClick={saveEditedNote}
                id="save-note-button"
                disabled={!editingNote}
              />
              <IconButton
                iconClass="fas fa-times"
                tooltip="Discard Changes"
                onClick={cancelEdit}
                id="cancel-note-button"
              />
            </div>
          ) : (
            <div>
              <IconButton
                id={`edit-note-${note.id}`}
                iconClass="fas fa-edit"
                tooltip={
                  isCurrentUserAuthor(note.author.email)
                    ? 'Edit Note'
                    : 'You cannot edit this note'
                }
                onClick={() => startEditNote(note)}
                disabled={
                  editedNoteId !== null ||
                  !isCurrentUserAuthor(note.author.email)
                }
              />
              <IconButton
                id={`delete-note-${note.id}`}
                iconClass="fas fa-trash"
                tooltip={
                  isCurrentUserAuthor(note.author.email)
                    ? 'Delete Note'
                    : 'You cannot delete this note'
                }
                onClick={() => confirmDeleteNote(note.id)}
                disabled={
                  editedNoteId !== null ||
                  !isCurrentUserAuthor(note.author.email)
                }
              />
            </div>
          )}
        </CardHeader>
        <CardBody>
          {editedNoteId === note.id ? (
            <div className="note-editor-container">
              <GenericRichEditor
                editorState={editorEditedState}
                setEditorState={setEditorEditedState}
                placeholder="Edit your note..."
              />
            </div>
          ) : (
            <div style={{ marginTop: '15px' }}>
              <Markdown>{note.content}</Markdown>
            </div>
          )}
        </CardBody>
      </Card>
    ));
  };

  // Prepare content for the modal
  const notesContent = (
    <CustomModalWrapper id="notes-scroll">
      {/* Notes List */}
      <h5>Existing Notes</h5>
      <CustomModalWrapper id="existing-notes-scroll" height="40vh">
        <div
          style={{
            minHeight: '40vh',
          }}
        >
          {renderNotesContent()}
        </div>
      </CustomModalWrapper>

      {/* Add Note Form */}
      <Card className="editorContainer">
        <br />
        <h5>Add a new note</h5>
        <GenericRichEditor
          editorState={editorState}
          setEditorState={setEditorState}
          placeholder="Type your note here..."
        />
      </Card>
    </CustomModalWrapper>
  );

  // Custom footer with Add Note button
  const customFooter = (
    <Row className="w-100">
      <Col className="text-left">
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
      </Col>
      <Col className="text-right">
        <Button
          color="primary"
          onClick={addNote}
          disabled={addingNote || editedNoteId !== null}
          style={{ minWidth: '100px' }}
        >
          {addingNote ? (
            <div className="d-flex align-items-center justify-content-center">
              <CircleSpinner size={20} />
              <span className="ml-1">Adding...</span>
            </div>
          ) : (
            'Add Note'
          )}
        </Button>
      </Col>
    </Row>
  );

  return (
    <>
      <ModalDialog
        title={`Notes for "${title}"`}
        onClose={toggle}
        childComponent={notesContent}
        customFooter={customFooter}
        size="lg"
      />
      {showConfirmDeleteModal && renderDeleteConfirmModal()}
    </>
  );
};

export default PCNotesModal;
